/**
 * CUSTOM.JS - Custom Script File
 * --------------------------------
 * ⚠️ Ensure this script is connected to the storefront.
 * To enable it, uncomment the <script> tag linking to this file in theme.liquid.
 * 
 * Example:
 * <!-- <script type="module" src="{{ 'custom.js' | asset_url }}"></script> --> ← Uncomment this line
 */

const blockIds = [
  'AZmJMM1FDYlZvT3VjM__afterpay_on_site_messaging_payments_messaging_GheLWR',
  'AUThtOHk5WENCOGRRZ__afterpay_on_site_messaging_payments_messaging_crKnbB',
  'AVklwQ1pEY2JvMDNOZ__afterpay_on_site_messaging_payments_messaging_GheLWR',
];

function applyStyles(blockId) {
  const shadowHost = document.querySelector('#placement-' + blockId);
  const shadowRoot = shadowHost?.shadowRoot;
  const outerBlock = document.querySelector(`[data-block-id^="${blockId}"]`);

  if (shadowRoot && outerBlock) {
    const logo = shadowRoot.querySelector('.afterpay-logo.brand-afterpay');
    const svg = shadowRoot.querySelector('.afterpay-logo-link .afterpay-logo svg');

    if (logo && svg) {
      svg.setAttribute('width', '70');
      svg.style.setProperty('width', '70px');
      logo.style.setProperty('width', 'auto');
      logo.style.setProperty('min-width', '0');
      logo.style.setProperty('min-height', '0');
      outerBlock.style.setProperty('margin-top', '0');
      outerBlock.style.setProperty('margin-bottom', '0');
      return true;
    }
  }
  return false;
}

function waitForBlocks() {
  let remaining = new Set(blockIds);

  // Try applying immediately for any already present blocks
  for (const blockId of blockIds) {
    if (applyStyles(blockId)) {
      remaining.delete(blockId);
    }
  }

  if (remaining.size === 0) return; // All done

  // Watch for new nodes added to DOM
  const observer = new MutationObserver(() => {
    for (const blockId of remaining) {
      if (applyStyles(blockId)) {
        remaining.delete(blockId);
      }
    }
    if (remaining.size === 0) {
      observer.disconnect();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForBlocks);
} else {
  waitForBlocks();
}

/**
 * FIX: Prevent quick-buy modals from opening twice on mobile
 * 
 * Issue: Two <quick-buy-modal> elements with the same ID exist in the DOM
 * (one for mobile, one for desktop layout), causing both to open simultaneously
 * when clicked on mobile.
 * 
 * Solution: Track show() calls globally and prevent duplicates happening
 * within 50ms, ensuring only one modal opens per click.
 * 
 * Metal Matching: Products with the 'metal-matching' tag will automatically
 * have their metal/material option matched to the customer's cart preference.
 */
(function preventDoubleModalOpenAndFixVariant() {
  // Cache for product metal matching configuration
  const metalProductsCache = new Map();
  
  // Cache for cart metal preference (short-lived)
  let cartPreferenceCache = null;
  let cartPreferenceCacheTime = 0;
  const CART_CACHE_DURATION = 5000; // 5 seconds
  
  // Track currently opening modal to prevent duplicates
  let currentlyOpenProductHandle = null;
  let isOpening = false;
  let openingPromise = null;
  
  /**
   * Detect metal type from text (handle or variant name)
   * @param {string} text - Text to analyze
   * @returns {string|null} 'gold' or 'silver' or null
   */
  function detectMetalType(text) {
    if (!text) return null;
    const lower = text.toLowerCase();
    
    // Check for silver keywords first (more specific patterns)
    if (lower.includes('sterling-silver') || 
        lower.includes('sterling silver') || 
        lower.includes('silver')) {
      return 'silver';
    }
    
    // Check for gold keywords (avoid matching "golden" as a color descriptor)
    // Match patterns like: "gold", "-gold", "gold-", "vermeil"
    if (lower.match(/\b(gold|vermeil)\b/) || 
        lower.includes('-gold') || 
        lower.includes('gold-')) {
      return 'gold';
    }
    
    return null;
  }
  
  /**
   * Fetch product data and check if metal matching is enabled via tag
   * @param {string} productHandle - Product handle
   * @returns {Promise<Object|null>} Configuration object or null if not eligible
   */
  async function getMetalMatchingConfig(productHandle) {
    // Check cache first
    if (metalProductsCache.has(productHandle)) {
      const cached = metalProductsCache.get(productHandle);
      return cached;
    }
    
    try {
      const product = await fetch(`${window.Shopify.routes.root}products/${productHandle}.js?t=${Date.now()}`).then(r => r.json());
      
      // Check if metal matching is enabled via tag
      const hasMetalMatchingTag = product.tags?.some(tag => 
        tag.toLowerCase() === 'metal-matching' || 
        tag.toLowerCase() === 'enable-metal-matching'
      );
      
      if (!hasMetalMatchingTag) {
        metalProductsCache.set(productHandle, null);
        return null;
      }
      
      // Check if this is a single-SKU product (no variants or only 1 variant)
      const isSingleSku = !product.options || product.options.length === 0 || 
                          (product.variants && product.variants.length === 1);
      
      if (isSingleSku) {
        // For single-SKU products, detect metal from product handle or title
        const metalType = detectMetalType(productHandle) || detectMetalType(product.title);
        
        const config = {
          position: null,
          metals: [],
          isSingleSku: true,
          metalType: metalType
        };
        
        metalProductsCache.set(productHandle, config);
        return config;
      }
      
      // Find the metal option (look for option containing "metal", "material", etc.)
      let metalOptionPosition = null;
      const metalKeywords = ['metal', 'material', 'finish'];
      
      if (product.options) {
        for (let i = 0; i < product.options.length; i++) {
          const option = product.options[i];
          // Options can be either strings or objects with a "name" property
          const optionName = (typeof option === 'string' ? option : option.name || option).toLowerCase();
          
          if (metalKeywords.some(keyword => optionName.includes(keyword))) {
            metalOptionPosition = i + 1; // Shopify uses 1-based indexing
            break;
          }
        }
      }
      
      if (!metalOptionPosition) {
        metalProductsCache.set(productHandle, null);
        return null;
      }
      
      // Extract available metal values from variants
      const metalValues = new Set();
      if (product.variants) {
        product.variants.forEach(variant => {
          const metalValue = variant[`option${metalOptionPosition}`];
          if (metalValue) {
            metalValues.add(metalValue.toLowerCase().replace(/\s+/g, '-'));
          }
        });
      }
      
      const config = {
        position: metalOptionPosition,
        metals: Array.from(metalValues)
      };
      
      metalProductsCache.set(productHandle, config);
      return config;
      
    } catch (error) {
      console.error(`[Metal Matching] Error fetching config for ${productHandle}:`, error);
      metalProductsCache.set(productHandle, null);
      return null;
    }
  }
  
  // Extract clean product handle without query parameters
  function getProductHandle(fullHandle) {
    return fullHandle ? fullHandle.split('?')[0] : null;
  }
  
  /**
   * Fetch cart and determine metal preference from most recent matching item
   * @returns {Promise<string|null>} Normalized metal type or null
   */
  async function getCartMetalPreference() {
    // Check cache first (5 second TTL)
    const now = Date.now();
    if (cartPreferenceCache !== null && (now - cartPreferenceCacheTime) < CART_CACHE_DURATION) {
      return cartPreferenceCache;
    }
    
    try {
      // Cache-bust to ensure fresh cart data
      const cart = await fetch(`${window.Shopify.routes.root}cart.js?t=${Date.now()}`).then(r => r.json());
      
      if (!cart.items || cart.items.length === 0) return null;
      
      // Shopify cart API returns newest items at index [0]
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        
        // Check if this item's product has metal matching enabled
        const config = await getMetalMatchingConfig(item.handle);
        if (!config) continue;
        
        // Handle single-SKU products: detect metal from the product itself
        if (config.isSingleSku && config.metalType) {
          cartPreferenceCache = config.metalType;
          cartPreferenceCacheTime = Date.now();
          return config.metalType;
        }
        
        // Handle variant-based products: get the metal option value
        if (config.position) {
          const metalValue = item.options_with_values?.[config.position - 1]?.value;
          if (!metalValue) continue;
          
          const metalLower = metalValue.toLowerCase();
          
          // Determine if this is gold or silver based metal type
          // Return a simple keyword that can be used for matching
          if (metalLower.includes('gold') || metalLower.includes('vermeil')) {
            cartPreferenceCache = 'gold';
            cartPreferenceCacheTime = Date.now();
            return 'gold';
          }
          if (metalLower.includes('silver') || metalLower.includes('sterling')) {
            cartPreferenceCache = 'silver';
            cartPreferenceCacheTime = Date.now();
            return 'silver';
          }
        }
      }
      
      cartPreferenceCache = null;
      cartPreferenceCacheTime = Date.now();
      return null;
    } catch (error) {
      console.error('[Metal Matching] Error getting cart metal preference:', error);
      return null;
    }
  }
  
  /**
   * Auto-select the matching metal option in the modal's variant picker
   * @param {HTMLElement} modalElement - The quick-buy modal element
   * @param {string} metalPreference - The preferred metal type ('gold' or 'silver')
   * @param {string} productHandle - Product handle
   */
  async function selectMatchingMetalOption(modalElement, metalPreference, productHandle) {
    if (!metalPreference) return;
    
    // Verify this product supports metal matching
    const config = await getMetalMatchingConfig(productHandle);
    if (!config || config.isSingleSku || !config.position) return;
    
    const variantPicker = modalElement.querySelector('variant-picker');
    if (!variantPicker) return;
    
    // Find all radio inputs for the metal option
    const metalInputs = variantPicker.querySelectorAll(`input[data-option-position="${config.position}"]`);
    if (metalInputs.length === 0) return;
    
    // Find and select the matching metal input
    for (const input of metalInputs) {
      const label = document.querySelector(`label[for="${input.id}"]`);
      const labelText = label ? label.textContent.trim().toLowerCase() : '';
      
      let shouldSelect = false;
      
      // Match based on simple keywords
      if (metalPreference === 'gold' && (labelText.includes('gold') || labelText.includes('vermeil'))) {
        shouldSelect = true;
      }
      if (metalPreference === 'silver' && (labelText.includes('silver') || labelText.includes('sterling'))) {
        shouldSelect = true;
      }
      
      if (shouldSelect && !input.checked) {
        input.checked = true;
        // Dispatch change event to update the variant picker (price, image, variant ID, etc.)
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      } else if (shouldSelect && input.checked) {
        return;
      }
    }
  }
  
  
  document.addEventListener('DOMContentLoaded', () => {
    // Pre-fetch configurations for all quick-buy modals on page load
    // This ensures instant matching when modals are opened
    const preFetchConfigs = async () => {
      const quickBuyModals = document.querySelectorAll('quick-buy-modal[handle]');
      
      const handles = Array.from(quickBuyModals).map(modal => {
        const fullHandle = modal.getAttribute('handle');
        return getProductHandle(fullHandle);
      }).filter(Boolean);
      
      // Remove duplicates
      const uniqueHandles = [...new Set(handles)];
      
      // Fetch configs in parallel for fast initialization
      await Promise.all(
        uniqueHandles.map(handle => getMetalMatchingConfig(handle).catch(() => null))
      );
    };
    
    // Pre-fetch after a short delay to not block initial page load
    setTimeout(preFetchConfigs, 500);
    
    // Intercept all clicks on quick-buy triggers
    document.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[aria-controls]');
      if (!trigger) return;
      
      const modalId = trigger.getAttribute('aria-controls');
      const modalElement = document.getElementById(modalId);
      if (!modalElement || modalElement.tagName !== 'QUICK-BUY-MODAL') return;
      
      const fullHandle = modalElement.getAttribute('handle');
      const productHandle = getProductHandle(fullHandle);
      
      // Strip variant parameter from handle to always open with first variant
      // This prevents pre-selecting specific variants (e.g., from complementary products)
      if (fullHandle && fullHandle.includes('?variant=')) {
        modalElement.setAttribute('handle', productHandle);
      }
      
      // Prevent double-open: if this product's modal is already opening, wait for it
      if (currentlyOpenProductHandle === productHandle && (openingPromise || isOpening)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (openingPromise) await openingPromise;
        return;
      }
      
      // Lock this product to prevent concurrent opens
      isOpening = true;
      currentlyOpenProductHandle = productHandle;
      event.preventDefault();
      event.stopImmediatePropagation();
      
      // Async opening sequence
      openingPromise = (async () => {
        try {
          // Start fetching cart preference immediately (parallel with modal opening)
          const cartPreferencePromise = getCartMetalPreference();
          const configPromise = getMetalMatchingConfig(productHandle);
          
          // Open the modal immediately
          if (typeof modalElement.show === 'function') {
            await modalElement.show();
          }
          
          // Check if this product supports metal matching
          const config = await configPromise;
          
          // For variant-based products, select the matching metal option
          // Cart preference can come from either variant-based OR single-SKU products
          if (config && !config.isSingleSku) {
            const metalPreference = await cartPreferencePromise;
            
            if (metalPreference) {
              // Reduced wait time - just enough for DOM to stabilize (20ms)
              await new Promise(resolve => setTimeout(resolve, 20));
              await selectMatchingMetalOption(modalElement, metalPreference, productHandle);
            }
          }
        } catch (error) {
          console.error('[Metal Matching] Error:', error);
        } finally {
          // Release lock immediately after modal opens
          setTimeout(() => {
            if (currentlyOpenProductHandle === productHandle) {
              isOpening = false;
              currentlyOpenProductHandle = null;
              openingPromise = null;
            }
          }, 100);
        }
      })();
    }, true); // Use capture phase to intercept before theme handlers
    
    // Clean up state when modal closes
    document.addEventListener('dialog:after-hide', (event) => {
      if (event.target.tagName === 'QUICK-BUY-MODAL') {
        const handle = event.target.getAttribute('handle');
        const productHandle = getProductHandle(handle);
        if (currentlyOpenProductHandle === productHandle) {
          currentlyOpenProductHandle = null;
        }
        isOpening = false;
        openingPromise = null;
      }
    });
    
    // Invalidate cart preference cache when cart changes
    document.addEventListener('cart:change', () => {
      cartPreferenceCache = null;
      cartPreferenceCacheTime = 0;
    });
    
    document.addEventListener('cart:refresh', () => {
      cartPreferenceCache = null;
      cartPreferenceCacheTime = 0;
    });
  });
})();

/**
 * DYNAMIC COMPLEMENTARY PRODUCTS HEADING
 * 
 * Updates complementary product headings on product pages when cart changes,
 * reflecting tiered discount messages based on cart contents (same as cart drawer).
 * 
 * How it works:
 * - Listens for cart:change events (fired when items added/removed)
 * - Listens for navigation events (popstate for browser back/forward)
 * - Re-fetches only the complementary products section via Shopify Section Rendering API
 * - Extracts and updates just the heading portion, keeping products intact
 * - Maintains smooth UX without full page reload
 */
(function dynamicComplementaryHeadings() {
  // Track if we're currently updating to prevent duplicate requests
  let isUpdating = false;
  
  async function updateComplementaryHeadings() {
    // Prevent concurrent updates
    if (isUpdating) return;
    
    // Find all complementary product sections on the page that should have dynamic updates
    // These will have a data-dynamic-headings="true" attribute set by Liquid when 
    // the product has the custom.show_persistent_complementary metafield enabled
    const complementaryElements = document.querySelectorAll('product-recommendations[intent="complementary"][data-dynamic-headings="true"]');
    
    if (complementaryElements.length === 0) return;
    
    isUpdating = true;
    
    try {
      // Process each complementary products section
      for (const element of complementaryElements) {
        const productId = element.getAttribute('data-product-id');
        const sectionId = element.getAttribute('data-section-id');
        const blockId = element.getAttribute('data-block-id');
        
        if (!productId || !sectionId) continue;
        
        // Fetch fresh section HTML with current cart state
        const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${element.getAttribute('limit') || 2}&section_id=${sectionId}&intent=complementary`;
        
        const response = await fetch(url);
        if (!response.ok) continue;
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Find the updated complementary products section
        const updatedElement = doc.querySelector('product-recommendations[intent="complementary"]');
        if (!updatedElement) continue;
        
        // Extract just the heading area (contains dynamic messages)
        const currentHeader = element.querySelector('.complementary-products__header');
        const updatedHeader = updatedElement.querySelector('.complementary-products__header');
        
        // Replace only the heading if both exist
        if (currentHeader && updatedHeader) {
          currentHeader.replaceWith(updatedHeader.cloneNode(true));
        }
      }
    } catch (error) {
      console.error('Error updating complementary product headings:', error);
    } finally {
      isUpdating = false;
    }
  }
  
  // Listen for cart changes (add, remove, update quantity)
  document.addEventListener('cart:change', updateComplementaryHeadings);
  // Also listen for direct cart refresh events
  document.addEventListener('cart:refresh', updateComplementaryHeadings);
  
  // Fix for browser navigation (back/forward buttons)
  // Listen for popstate events when user navigates through browser history
  window.addEventListener('popstate', updateComplementaryHeadings);
  
  // Also update on page load to ensure correct state
  document.addEventListener('DOMContentLoaded', updateComplementaryHeadings);
  
  // Add support for the pageshow event which fires when page is restored from bfcache
  window.addEventListener('pageshow', (event) => {
    // If the page was restored from bfcache, we need to update the headings
    if (event.persisted) {
      updateComplementaryHeadings();
    }
  });
})();

/**
 * CART NOTIFICATION BAR
 * 
 * Displays a sticky notification bar below the header when:
 * - Customer has eligible products in cart
 * - A threshold message is available
 * 
 * Features:
 * - Smooth slide-down animation from header
 * - Quick access button to open Quick Buy modal for complementary products
 * - Dismissible (stores preference in sessionStorage)
 * - Updates dynamically when cart changes
 * - Inherits all theme styles for consistency
 * - Dynamically injects itself on first cart add
 */
class CartNotificationBar extends HTMLElement {
  constructor() {
    super();
    
    this.closeButton = this.querySelector('.cart-notification-bar__close');
    this.shopButton = this.querySelector('.cart-notification-bar__button');
  }
  
  connectedCallback() {
    // Always show bar when it's rendered (no sessionStorage dismissal)
    this.show();
    
    // Handle close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.dismiss());
    }
    
    // Shop button is handled by theme's native aria-controls system
    // No custom handler needed - theme automatically opens modals
    
    // Listen for cart changes to update/hide bar
    document.addEventListener('cart:change', () => this.handleCartChange());
    document.addEventListener('cart:refresh', () => this.handleCartChange());
    
    // Fix for browser navigation (back/forward buttons)
    // Listen for popstate events when user navigates through browser history
    window.addEventListener('popstate', () => this.handleCartChange());
    
    // Add support for the pageshow event which fires when page is restored from bfcache
    window.addEventListener('pageshow', (event) => {
      // If the page was restored from bfcache, we need to update
      if (event.persisted) {
        this.handleCartChange();
      }
    });
  }
  
  dismiss() {
    // Simply hide the bar - no sessionStorage
    this.classList.remove('is-visible');
    setTimeout(() => {
      this.classList.add('is-hidden');
    }, 300);
  }
  
  show() {
    // Remove hidden state first (if present)
    if (this.classList.contains('is-hidden')) {
      this.classList.remove('is-hidden');
      // Force reflow to ensure transition happens
      void this.offsetHeight;
    }
    // Add visible class for smooth animation
    requestAnimationFrame(() => {
      this.classList.add('is-visible');
    });
  }
  
  async openQuickBuy() {
    // The Quick Buy modal is now triggered automatically via aria-controls
    // This method is kept for backwards compatibility but is no longer needed
    // as the theme's built-in modal system handles the opening
  }
  
  async handleCartChange() {
    // Re-fetch the notification bar to check if message has changed or should be hidden
    try {
      const response = await fetch(window.location.href);
      if (!response.ok) return;
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const updatedBar = doc.querySelector('cart-notification-bar');
      
      // If no updated bar exists, hide current one
      if (!updatedBar) {
        this.classList.remove('is-visible');
        setTimeout(() => this.classList.add('is-hidden'), 300);
        return;
      }
      
      // Bar exists - make sure it's visible
      if (this.classList.contains('is-hidden')) {
        this.show();
      } else if (!this.classList.contains('is-visible')) {
        this.show();
      }
      
      // Check if message has changed
      const currentMessage = this.querySelector('.cart-notification-bar__text')?.textContent?.trim();
      const updatedMessage = updatedBar.querySelector('.cart-notification-bar__text')?.textContent?.trim();
      
      if (currentMessage !== updatedMessage) {
        // Message changed - update content
        const currentContent = this.querySelector('.cart-notification-bar__content');
        const updatedContent = updatedBar.querySelector('.cart-notification-bar__content');
        
        if (currentContent && updatedContent) {
          currentContent.replaceWith(updatedContent.cloneNode(true));
          // Re-attach event listeners
          this.closeButton = this.querySelector('.cart-notification-bar__close');
          this.shopButton = this.querySelector('.cart-notification-bar__button');
          if (this.closeButton) {
            this.closeButton.addEventListener('click', () => this.dismiss());
          }
        }
      }
    } catch (error) {
      console.error('Error updating cart notification bar:', error);
    }
  }
}

// Register custom element
if (!customElements.get('cart-notification-bar')) {
  customElements.define('cart-notification-bar', CartNotificationBar);
}

/**
 * CART NOTIFICATION BAR MANAGER
 * 
 * Global listener that handles dynamic injection of the notification bar
 * when cart changes, even if the bar didn't exist on page load (empty cart).
 * 
 * This ensures the bar appears immediately on first add-to-cart without
 * requiring a page refresh.
 */
(function cartNotificationBarManager() {
  let isUpdating = false;
  
  async function injectOrUpdateNotificationBar() {
    // Prevent concurrent updates
    if (isUpdating) return;
    isUpdating = true;
    
    try {
      // Fetch fresh page HTML to get updated notification bar snippet
      const response = await fetch(window.location.href);
      if (!response.ok) return;
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const updatedBar = doc.querySelector('cart-notification-bar');
      const currentBar = document.querySelector('cart-notification-bar');
      
      // Case 1: Bar should exist but doesn't (first add to cart)
      if (updatedBar && !currentBar) {
        // Find the insertion point (after header)
        const header = document.querySelector('header');
        if (header) {
          // Also inject the quick-buy-modal if it exists
          const updatedModal = doc.querySelector('quick-buy-modal[id^="cart-notification-modal-"]');
          
          // Ensure bar starts in hidden state for smooth animation
          updatedBar.classList.remove('is-visible');
          updatedBar.classList.add('is-hidden');
          
          // Insert bar after header
          header.insertAdjacentElement('afterend', updatedBar);
          
          // Insert modal at end of body if it exists
          if (updatedModal) {
            document.body.appendChild(updatedModal);
          }
          
          // Force a reflow to ensure hidden state is applied before transition
          void updatedBar.offsetHeight;
          
          // Wait for next frame, then trigger the show animation
          // This ensures CSS transition fires properly
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              updatedBar.classList.remove('is-hidden');
              updatedBar.classList.add('is-visible');
            });
          });
        }
      }
      // Case 2: Bar exists and should be updated (handled by CartNotificationBar.handleCartChange)
      // Case 3: Bar exists but shouldn't (will be handled by CartNotificationBar.handleCartChange)
      
      // Update existing bar if present
      if (updatedBar && currentBar) {
        // Check if messages have changed
        const currentMessage = currentBar.querySelector('.cart-notification-bar__text')?.textContent?.trim();
        const updatedMessage = updatedBar.querySelector('.cart-notification-bar__text')?.textContent?.trim();
        
        // If the message has changed, trigger the handleCartChange method on the current bar
        if (currentMessage !== updatedMessage && typeof currentBar.handleCartChange === 'function') {
          currentBar.handleCartChange();
        }
      }
    } catch (error) {
      console.error('Error injecting cart notification bar:', error);
    } finally {
      isUpdating = false;
    }
  }
  
  // Listen for cart changes globally
  document.addEventListener('cart:change', injectOrUpdateNotificationBar);
  document.addEventListener('cart:refresh', injectOrUpdateNotificationBar);
  
  // Fix for browser navigation (back/forward buttons)
  // Listen for popstate events when user navigates through browser history
  window.addEventListener('popstate', injectOrUpdateNotificationBar);
  
  // Also update on page load to ensure correct state
  document.addEventListener('DOMContentLoaded', injectOrUpdateNotificationBar);
  
  // Add support for the pageshow event which fires when page is restored from bfcache
  window.addEventListener('pageshow', (event) => {
    // If the page was restored from bfcache, we need to update the bar
    if (event.persisted) {
      injectOrUpdateNotificationBar();
    }
  });
})();

/**
 * ============================================================================
 * FEATURE WITH ICON VARIANT - Dynamic Variant-Based Feature Rendering
 * ============================================================================
 * 
 * This module handles re-rendering of feature_with_icon_variant blocks when
 * the product variant changes. It uses the Shopify Section Rendering API to
 * fetch updated content based on the selected variant.
 */
(function() {
  'use strict';

  /**
   * Fetches and updates variant-based feature blocks for a specific section
   * @param {string} sectionId - The ID of the section to re-render
   * @param {number} variantId - The selected variant ID
   */
  async function updateVariantFeatureBlocks(sectionId, variantId) {
    // Find all variant feature blocks in the current section
    const section = document.querySelector(`#shopify-section-${sectionId}`);
    if (!section) return;

    const variantFeatureBlocks = section.querySelectorAll('[data-variant-feature-block]');
    if (variantFeatureBlocks.length === 0) return;

    try {
      // Fetch the section HTML with the new variant
      const url = `${window.location.pathname}?section_id=${sectionId}&variant=${variantId}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error('[Variant Feature Blocks] Failed to fetch section:', response.statusText);
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // The fetched HTML is just the section content, not wrapped in shopify-section
      const newVariantBlocks = doc.querySelectorAll('[data-variant-feature-block]');

      // Update each variant feature block by matching block IDs
      variantFeatureBlocks.forEach(oldBlock => {
        const blockId = oldBlock.dataset.blockId;
        if (!blockId) return;
        
        // Find the corresponding block in the new HTML by block ID
        const newBlock = Array.from(newVariantBlocks).find(block => block.dataset.blockId === blockId);
        
        if (newBlock) {
          // Safari-compatible replacement: use parentNode.replaceChild instead of replaceWith
          if (oldBlock.parentNode) {
            oldBlock.parentNode.replaceChild(newBlock, oldBlock);
          }
        } else {
          oldBlock.style.display = 'none';
        }
      });

    } catch (error) {
      console.error('[Variant Feature Blocks] Error updating variant feature blocks:', error);
    }
  }

  /**
   * Initialize variant change listeners for all product forms
   */
  function initVariantFeatureBlocks() {
    // Listen for variant:change events on all product forms
    document.addEventListener('variant:change', async (event) => {
      const form = event.target;
      
      // Get the variant data from the event
      const { variant } = event.detail;
      if (!variant) return;

      // Find the parent Shopify section
      const section = form.closest('.shopify-section');
      if (!section) return;

      // Extract section ID from the shopify-section id attribute
      const sectionId = section.id.replace('shopify-section-', '');
      if (!sectionId) return;

      // Update the variant feature blocks
      await updateVariantFeatureBlocks(sectionId, variant.id);
    });
    
    // Also listen for change events on variant picker inputs as a fallback for Safari
    document.addEventListener('change', async (event) => {
      const target = event.target;
      
      // Check if this is a variant picker input
      if (target && target.hasAttribute && target.hasAttribute('data-option-position')) {
        // Find the form
        const formId = target.getAttribute('form');
        if (!formId) return;
        
        const form = document.getElementById(formId);
        if (!form) return;
        
        // Wait a bit for the variant to update
        setTimeout(async () => {
          // Find the variant ID from the form's hidden input
          const variantInput = form.querySelector('input[name="id"]');
          if (!variantInput || !variantInput.value) return;
          
          const variantId = variantInput.value;
          
          // Find the parent Shopify section
          const section = form.closest('.shopify-section');
          if (!section) return;
          
          const sectionId = section.id.replace('shopify-section-', '');
          if (!sectionId) return;
          
          await updateVariantFeatureBlocks(sectionId, variantId);
        }, 100);
      }
    }, true); // Use capture phase
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantFeatureBlocks);
  } else {
    initVariantFeatureBlocks();
  }
})();
